import { get, isEqual, isPlainObject } from "lodash"

const KEY_PREFIX = "pi:simulator-trades"

export type TradeContextValue = string | number | null
export type TradeContext = Record<string, TradeContextValue>

export interface StoredSimulatorTrades<Trade = unknown, Base = unknown> {
  context: TradeContext
  trades: Trade[]
  actionsBase: Base | null
}

export interface TradeStorageScope {
  userId: string
  accountId: string
}

function readNonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null
}

export function getTradeStorageScope(state: unknown): TradeStorageScope | null {
  const userId = readNonEmptyString(get(state, "user.id"))
  const accountId = readNonEmptyString(get(state, "global.selected.accountId"))
  return userId && accountId ? { userId, accountId } : null
}

function getScopedPrefix({ userId, accountId }: TradeStorageScope): string {
  return `${KEY_PREFIX}:${userId}:${accountId}`
}

export function getPortfolioTradesKey(scope: TradeStorageScope, portfolioId: string): string {
  return `${getScopedPrefix(scope)}:portfolio:${portfolioId}`
}

export function getDashboardTradesKey(scope: TradeStorageScope, dashboardId: string): string {
  return `${getScopedPrefix(scope)}:dashboard:${dashboardId}`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return isPlainObject(value)
}

function isTradeContextValue(value: unknown): value is TradeContextValue {
  return value === null || typeof value === "string" || typeof value === "number"
}

function parseTradeContext(value: unknown): TradeContext | null {
  if (!isRecord(value)) return null
  const context: TradeContext = {}
  for (const [field, fieldValue] of Object.entries(value)) {
    if (!isTradeContextValue(fieldValue)) return null
    context[field] = fieldValue
  }
  return context
}

export function readSimulatorTrades(key: string): StoredSimulatorTrades | null {
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed)) return null
    const context = parseTradeContext(parsed.context)
    const { trades, actionsBase } = parsed
    if (!context || !Array.isArray(trades) || trades.length === 0) return null
    return { context, trades, actionsBase: actionsBase ?? null }
  } catch {
    return null
  }
}

export function writeSimulatorTrades(key: string, entry: StoredSimulatorTrades): boolean {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(entry))
    return true
  } catch {
    return false
  }
}

export function removeSimulatorTrades(key: string): void {
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    return
  }
}

export function isSameTradeOwner<Context extends TradeContext>(
  previous: TradeContext,
  next: Context,
  owningFields: readonly (keyof Context & string)[],
): boolean {
  return owningFields.every(field => previous[field] === next[field])
}

export interface MirroredStore<State> {
  getState: () => State
  subscribe: (listener: () => void) => () => void
}

export interface TradeMirrorSource<State, Context extends TradeContext, Trade, Base> {
  selectTrades: (state: State) => Trade[] | undefined
  selectActionsBase: (state: State) => Base | null
  selectContext: (state: State) => Context
  getKey: (context: Context) => string | null
  owningFields: readonly (keyof Context & string)[]
}

export function subscribeTradeMirror<State, Context extends TradeContext, Trade, Base>(
  store: MirroredStore<State>,
  source: TradeMirrorSource<State, Context, Trade, Base>,
): () => void {
  const initialState = store.getState()
  let lastTrades = source.selectTrades(initialState)
  let lastBase = source.selectActionsBase(initialState)
  let lastContext = source.selectContext(initialState)
  let tradesOwner: Context | null = lastTrades?.length ? lastContext : null
  let writtenKey = tradesOwner ? source.getKey(tradesOwner) : null

  const forget = () => {
    if (writtenKey) removeSimulatorTrades(writtenKey)
    writtenKey = null
  }

  return store.subscribe(() => {
    const state = store.getState()
    const trades = source.selectTrades(state)
    const actionsBase = source.selectActionsBase(state)
    const context = source.selectContext(state)
    const tradesChanged = trades !== lastTrades || actionsBase !== lastBase
    if (!tradesChanged && isEqual(context, lastContext)) return
    lastTrades = trades
    lastBase = actionsBase
    lastContext = context

    if (!trades?.length) {
      tradesOwner = null
      forget()
      return
    }

    if (tradesChanged) tradesOwner = context
    const key = tradesOwner && isSameTradeOwner(tradesOwner, context, source.owningFields) ? source.getKey(context) : null
    if (!key) {
      forget()
      return
    }
    if (writtenKey && writtenKey !== key) removeSimulatorTrades(writtenKey)
    if (writeSimulatorTrades(key, { context, trades, actionsBase })) {
      writtenKey = key
    } else {
      removeSimulatorTrades(key)
      writtenKey = null
    }
  })
}
